'use client'

import Image from 'next/image'
import posthog from 'posthog-js'
import { unstable_rethrow, usePathname } from 'next/navigation'
import { logger } from '@/utils/logger'
import { Component, ReactNode, ErrorInfo } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /** Changes on navigation. A new value clears a caught error so the next
   *  route renders instead of the fallback staying up across the whole
   *  layout. Supplied by the ErrorBoundary wrapper below. */
  resetKey?: string
}

interface State {
  hasError: boolean
  error?: Error
}

class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // notFound() and redirect() must reach Next's boundaries, or a 404 renders as this crash page
    unstable_rethrow(error)
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })

    try {
      posthog.captureException(error, { componentStack: errorInfo.componentStack })
    } catch {
      // PostHog is deferred and may not be loaded yet; the local log above still records the error.
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined })
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <section className="errorpage py-16">
          <div className="container">
            <div className="flex flex-col justify-center lg:mx-auto lg:max-w-3xl">
              <div className="content">
                <h1 className="text-[calc(2.325rem+.9vw)] tracking-[-1.28px] xl:text-[4rem]">Something went wrong</h1>
                <p className="mb-8 mt-2 text-primary-ink-60">
                  An error occurred while rendering this component. We're working to fix it.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-default btn-primary btn-interaction">
                  Try Again
                  <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )
    }

    return this.props.children
  }
}

/**
 * Route-aware error boundary. Wraps the class boundary and feeds it the current
 * pathname so a caught error clears on navigation — without this the fallback
 * stays up across the whole shared layout until a full page reload.
 */
export function ErrorBoundary(props: Omit<Props, 'resetKey'>) {
  const pathname = usePathname()
  return <ErrorBoundaryInner {...props} resetKey={pathname ?? undefined} />
}
